/* eslint-disable @typescript-eslint/no-explicit-any */

import { AxiosResponse } from 'axios';
import { call, delay, put } from 'redux-saga/effects';

import api from '../../api/api';
import { errorHandling } from '../../api/errorHandling';
import { mapSuccessResponseToState } from '../../api/responseHandling';
import { getConfigStorageItem } from '../../hooks/useConfigStorage';
import i18n from '../../utils/i18n';
import { getApiVersion } from '../apiVersion';
import { PageNameType } from '../clientConfiguration/clientConfigurationSlice';
import reduxSlicesList from '../reduxSlicesList';

export function* getTable(props: any) {
 const slice = reduxSlicesList[props.payload.table as PageNameType];
 try {
  const { table, url, ...rest } = props.payload;
  const version = getApiVersion(table, 'getTable');
  const tableName = table.charAt(0).toUpperCase() + table.slice(1);
  const response: AxiosResponse<any[]> = yield call(
   api.post,
   url || `/${tableName}/v${version}`,
   rest,
  );
  yield put(
   slice.getTableSuccess(
    mapSuccessResponseToState(response, tableName, 'fetched'),
   ),
  );
  yield delay(0);
  yield put(slice.getTableFinish());
 } catch (error: unknown) {
  console.log('error', error);
  yield* errorHandling(error, slice.getTableFailure);
 }
}

export function* getItem(props: any) {
 const slice =
  reduxSlicesList[props.payload.table as keyof typeof reduxSlicesList];
 try {
  const { table, id } = props.payload;
  const version = getApiVersion(table, 'getItem');
  const tableName = table.charAt(0).toUpperCase() + table.slice(1);
  const response: AxiosResponse<any[]> = yield call(
   api.get,
   `/${tableName}/v${version}/${id}`,
  );

  yield put(
   slice.getItemSuccess(
    mapSuccessResponseToState(response, tableName, 'fetched'),
   ),
  );
  yield delay(0);
  yield put(slice.getItemFinish());
 } catch (error: unknown) {
  yield* errorHandling(error, slice.getItemFailure);
 }
}

export function* createItem(props: any) {
 const slice =
  reduxSlicesList[props.payload.table as keyof typeof reduxSlicesList];
 try {
  const { table, data } = props.payload;
  const version = getApiVersion(table, 'createItem');
  const tableName = table.charAt(0).toUpperCase() + table.slice(1);
  const response: AxiosResponse<any[]> = yield call(
   api.post,
   `/${tableName}/v${version}/create`,
   props.payload.data,
  );

  yield put(
   slice.createItemSuccess(
    mapSuccessResponseToState(response, 'Table', 'created'),
   ),
  );
  yield delay(0);
  yield put(slice.createItemFinish());
 } catch (error: unknown) {
  yield* errorHandling(error, slice.createItemFailure);
 }
}

export function* updateItem(props: any) {
 const slice =
  reduxSlicesList[props.payload.table as keyof typeof reduxSlicesList];
 try {
  const { table, id, data, isPut } = props.payload;
  const version = getApiVersion(table, 'updateItem');
  const tableName = table.charAt(0).toUpperCase() + table.slice(1);

  const response: AxiosResponse<any[]> = yield call(
   isPut ? api.put : api.patch,
   `/${tableName}/v${version}/${isPut ? 'update' : 'patch'}/${id}`,
   data,
  );

  yield put(
   slice.updateItemSuccess(
    mapSuccessResponseToState(response, tableName, 'updated'),
   ),
  );
  yield delay(0);
  yield put(slice.updateItemFinish());
 } catch (error: unknown) {
  yield* errorHandling(error, slice.updateItemFailure);
 }
}

export function* deleteItems(props: any) {
 const slice =
  reduxSlicesList[props.payload.table as keyof typeof reduxSlicesList];
 try {
  const { table, ids } = props.payload;
  const version = getApiVersion(table, 'deleteItems');
  const tableName = table.charAt(0).toUpperCase() + table.slice(1);
  const response: AxiosResponse<any[]> = yield call(
   api.del,
   `/${tableName}/v${version}/abort`,
   ids,
  );

  yield put(
   slice.deleteItemsSuccess(
    mapSuccessResponseToState(response, tableName, 'deleted'),
   ),
  );
  yield delay(0);
  yield put(slice.deleteItemsFinish());
 } catch (error: unknown) {
  yield* errorHandling(error, slice.deleteItemsFailure);
 }
}

export function* deleteItem(props: any) {
 const slice =
  reduxSlicesList[props.payload.table as keyof typeof reduxSlicesList];
 try {
  const { table, id } = props.payload;
  const version = getApiVersion(table, 'deleteItem');
  const tableName = table.charAt(0).toUpperCase() + table.slice(1);
  const response: AxiosResponse<any[]> = yield call(
   api.del,
   `/${tableName}/v${version}/delete/${id}`,
   {},
  );

  yield put(
   slice.deleteItemSuccess(
    mapSuccessResponseToState(response, tableName, 'deleted'),
   ),
  );
  yield delay(0);
  yield put(slice.deleteItemFinish());
 } catch (error: unknown) {
  yield* errorHandling(error, slice.deleteItemFailure);
 }
}

export function* getTableStructure(props: any) {
 const slice =
  reduxSlicesList[props.payload.table as keyof typeof reduxSlicesList];
 try {
  const { table } = props.payload;
  const version = getApiVersion(table, 'getTableStructure');
  const tableName = table.charAt(0).toUpperCase() + table.slice(1);
  const structureObj = getConfigStorageItem(tableName)?.structure || [];
  if (structureObj.length === 0) {
   const response: AxiosResponse<any[]> = yield call(
    api.get,
    `/${tableName}/v${version}/structure?lang=${
     i18n.language ? i18n.language : 'lt'
    }`,
   );

   // it is saved to localStorage in tables page template
   yield put(
    slice.getTableStructureSuccess(
     mapSuccessResponseToState(response, `${tableName} structure`, 'fetched'),
    ),
   );
   yield delay(0);
   yield put(slice.getTableStructureFinish());
  } else {
   yield put(
    slice.getTableStructureSuccess({
     data: structureObj,
    }),
   );
   yield delay(0);
   yield put(slice.getTableStructureFinish());
  }
 } catch (error: unknown) {
  yield* errorHandling(error, slice.getTableStructureFailure);
 }
}

export function* getItemDocuments(props: any) {
 const { table, id } = props.payload;
 const slice = reduxSlicesList[table as keyof typeof reduxSlicesList];
 const tableCapitalized = table.charAt(0).toUpperCase() + table.slice(1);
 const version = getApiVersion(table, 'getItemDocuments');

 try {
  const response: AxiosResponse<any[]> = yield call(
   api.get,
   `/${tableCapitalized}/v${version}/${id}/documents`,
  );

  yield put(
   slice.getItemDocumentsSuccess(
    mapSuccessResponseToState(response, 'Documents', 'fetched'),
   ),
  );
 } catch (error: unknown) {
  yield* errorHandling(error, slice.getItemDocumentsFailure);
 }
}

export function* uploadItemDocuments(props: any) {
 const { table, id, files } = props.payload;
 const slice = reduxSlicesList[table as keyof typeof reduxSlicesList];
 const tableCapitalized = table.charAt(0).toUpperCase() + table.slice(1);
 const version = getApiVersion(table, 'uploadItemDocuments');

 try {
  const formData = new FormData();
  Array.from(files).forEach((f: any, i: number) => {
   formData.append(`uploads[${i}].File`, f);
   formData.append(`uploads[${i}].Name`, f.name || 'noname');
  });

  const response: AxiosResponse<any[]> = yield call(
   api.post,
   `/${tableCapitalized}/v${version}/${id}/documents/upload`,
   formData,
  );

  yield put(
   slice.uploadItemDocumentsSuccess(
    mapSuccessResponseToState(response, 'Documents', 'uploaded'),
   ),
  );
  yield delay(0);
  yield put(slice.uploadItemDocumentsFinish());
 } catch (error: unknown) {
  yield* errorHandling(error, slice.uploadItemDocumentsFailure);
 }
}

export function* deleteItemDocument(props: any) {
 const { table, id, documentId } = props.payload;
 const slice = reduxSlicesList[table as keyof typeof reduxSlicesList];
 const tableCapitalized = table.charAt(0).toUpperCase() + table.slice(1);
 const version = getApiVersion(table, 'deleteItemDocument');
 try {
  const response: AxiosResponse<any[]> = yield call(
   api.del,
   `/${tableCapitalized}/v${version}/${id}/documents/delete/${documentId}`,
   {},
  );

  yield put(
   slice.deleteItemDocumentSuccess(
    mapSuccessResponseToState(response, 'Document', 'deleted'),
   ),
  );
  yield delay(0);
  yield put(slice.deleteItemDocumentFinish());
 } catch (error: unknown) {
  yield* errorHandling(error, slice.deleteItemDocumentFailure);
 }
}

export function* getTableExcel(props: any) {
 const slice =
  reduxSlicesList[props.payload.table as keyof typeof reduxSlicesList];
 try {
  const { table, ...rest } = props.payload;
  const version = getApiVersion(table, 'getTableExcel');
  const tableName = table.charAt(0).toUpperCase() + table.slice(1);
  const response: AxiosResponse<any> = yield call(
   api.post,
   `/${tableName}/v${version}/excel`,
   { ...rest, lang: i18n.language ? i18n.language : 'lt', limit: 10000 },
  );
  const { data, ...restResponse } = mapSuccessResponseToState(
   response,
   tableName,
   'fetched',
  );
  yield put(
   slice.getTableExcelSuccess({
    ...restResponse,
    excel: {
     name: data.Name,
     type: data.Type,
     file: data.File,
     extension: data.Extension,
    },
   }),
  );
  yield delay(0);
  yield put(slice.getTableExcelFinish());
 } catch (error: unknown) {
  yield* errorHandling(error, slice.getTableExcelFailure);
 }
}
